import { useState, useEffect } from "react";
import { Input } from "@/components/ui/shadcn/input";
import { Switch } from "@/components/ui/shadcn/switch";
import { Slider } from "@/components/ui/shadcn/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import ColorPicker from "@/components/ui/ColorPicker";
import DragNumberInput from "@/components/ui/DragNumberInput";
import MiniAssetPicker from "@/components/AiModelInput/MiniAssetPicker";
import { ElementsRow, isPendingUpload } from "@/components/AiModelInput";
import useFileUpload from "@/hooks/useFileUpload";
import { useModal } from "@/contexts/ModalContext";
import AssetPreviewModal from "@/components/modals/AssetPreviewModal";
import ImagePreviewModal from "@/components/modals/ImagePreviewModal";
import { tabsForNodeType, WORLD_TABS } from "./propertiesTabs";
import { QUALITY_LEVELS } from "./renderQuality";
import {
  DEFAULT_Z_DEPTH_DISTANCE,
  DEFAULT_LIGHT_VISIBLE_SIZE,
} from "./sceneGraph";
import { GEN_AI_MODELS } from "@kagami/shared/sceneRenderModels";

const AI_REFERENCE_IMAGES_MAX = 5;

const AXES = ["x", "y", "z"];
const LABEL_MIN_WIDTH = "min-w-24";
const COLOR_SWATCH_SIZE = "w-8 h-8";

export const ASPECT_PRESETS = [
  { label: "16:9", value: 16 / 9 },
  { label: "4:3", value: 4 / 3 },
  { label: "1:1", value: 1 },
  { label: "9:16", value: 9 / 16 },
  { label: "21:9", value: 21 / 9 },
];

export const closestAspectLabel = (aspect) =>
  ASPECT_PRESETS.reduce((best, preset) =>
    Math.abs(preset.value - aspect) < Math.abs(best.value - aspect)
      ? preset
      : best,
  ).label;

// The camera's aspect field stores a plain ratio (see Viewport.jsx's
// `cam.aspect = n.aspect || 16/9`), while the gen-AI model config expresses
// aspect as a "W:H" string — these convert between the two so the dropdown
// can be driven directly by whichever model is selected for the scene.
export const ratioFromAspectKey = (key) => {
  const [w, h] = key.split(":").map(Number);
  return w / h;
};

export const closestAspectKey = (ratio, keys) =>
  keys.reduce((best, key) =>
    Math.abs(ratioFromAspectKey(key) - ratio) <
    Math.abs(ratioFromAspectKey(best) - ratio)
      ? key
      : best,
  );

// Every settings row shares this layout: a label in a fixed-minimum-width
// column on the left, its control filling the rest of the line — so a mesh's
// Roughness slider lines up under its Color swatch above it instead of each
// row indenting its control by however wide that row's own label happens to be.
function PropertyRow({ label, htmlFor, children }) {
  return (
    <div className="flex items-center gap-2">
      <label
        htmlFor={htmlFor}
        className={`text-[10px] text-secondary-foreground/50 shrink-0 ${LABEL_MIN_WIDTH}`}>
        {label}
      </label>
      <div className="flex-1 min-w-0 flex items-center gap-2">{children}</div>
    </div>
  );
}

// Fixed row of every category the current selection has properties for
// (World gets WORLD_TABS, a node gets tabsForNodeType(node.type) — see
// propertiesTabs.js) — sits above the scrollable content so switching
// category never scrolls the tab row itself out of view. Mirrors the same
// tab set already reachable per-row from the hierarchy panel's small icon
// buttons; this is the same state (`activeTab`/`onSelectTab`), just a second,
// more discoverable way to reach it once something is already selected.
function PropertiesTabBar({ tabs, activeTab, onSelectTab }) {
  return (
    <div
      id="Scene3DPropertiesPanel__tabs"
      className="flex items-center gap-1 px-2 pt-2 border-b border-secondary/40 flex-shrink-0">
      {tabs.map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          id={`Scene3DPropertiesPanel__tabTrigger-${id}`}
          type="button"
          onClick={() => onSelectTab(id)}
          title={label}
          className={`flex items-center gap-1 px-2 py-1.5 -mb-px border-b-2 text-xs cursor-pointer ${
            activeTab === id
              ? "border-primary text-primary"
              : "border-transparent text-secondary-foreground/60 hover:text-secondary-foreground"
          }`}>
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden xl:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}

// Mirrors the slider's own value, editable directly — typing is filtered to
// digits/minus/dot as you go, and clamped to the slider's own min/max once it
// parses to a real number so the two controls can never disagree.
function SliderValueInput({ value, onChange, min, max, decimals }) {
  const [draft, setDraft] = useState(() => value.toFixed(decimals));
  const [focused, setFocused] = useState(false);

  // Stays in sync with drags on the slider itself while this input isn't the
  // one being typed into.
  useEffect(() => {
    if (!focused) setDraft(value.toFixed(decimals));
  }, [value, decimals, focused]);

  const commitIfValid = (text) => {
    const trimmed = text.trim();
    const num = Number(trimmed);
    if (trimmed === "" || !Number.isFinite(num)) return;
    onChange(Math.min(max, Math.max(min, num)));
  };

  return (
    <input
      type="text"
      inputMode="decimal"
      value={draft}
      onFocus={(e) => {
        setFocused(true);
        e.target.select();
      }}
      onChange={(e) => {
        const filtered = e.target.value.replace(/[^0-9.\-]/g, "");
        setDraft(filtered);
        commitIfValid(filtered);
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") e.currentTarget.blur();
      }}
      onBlur={() => {
        setFocused(false);
        // Reformats a valid entry, and discards anything left over that
        // never parsed (e.g. a lone "-" or an empty field).
        setDraft(value.toFixed(decimals));
      }}
      className="text-[10px] w-10 h-6 px-1 text-right rounded-sm border border-secondary-foreground/20 bg-secondary/30 outline-none focus:border-primary/50 tabular-nums shrink-0"
    />
  );
}

function SliderField({
  label,
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.01,
  decimals = 2,
}) {
  return (
    <PropertyRow label={label}>
      <Slider
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={([v]) => onChange(v)}
      />
      <SliderValueInput
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        decimals={decimals}
      />
    </PropertyRow>
  );
}

function Vec3Field({ id, label, value, onChange, step = 0.1 }) {
  return (
    <div>
      <label className="text-[10px] text-secondary-foreground/50">
        {label}
      </label>
      <div className="grid grid-cols-3 gap-1">
        {AXES.map((axis) => (
          <DragNumberInput
            key={axis}
            id={`Scene3DProperties__transformField-${id}-${axis}`}
            step={step}
            value={value?.[axis] ?? 0}
            onChange={(num) => onChange(axis, num)}
          />
        ))}
      </div>
    </div>
  );
}

// Resolves a MiniAssetPicker value into `{key, url}` for persisting on a node
// or in sceneSettings — uploads eagerly (unlike aiReferenceImages, which
// defer to save time) since a texture drives what's live-rendered in the
// viewport right away, and THREE.TextureLoader needs a real URL to load, not
// a local blob wrapper.
async function resolveTextureValue(value, uploadFile) {
  if (!value) return { key: null, url: null };
  if (isPendingUpload(value)) {
    const result = await uploadFile(value.file, "textures");
    return result?.url
      ? { key: result.key, url: result.url }
      : { key: null, url: null };
  }
  const url = typeof value === "string" ? value : value.url;
  return { key: null, url: url ?? null };
}

// Already-uploaded values render through the rich AssetPreviewModal; pending
// (not-yet-uploaded) local files use the lightweight ImagePreviewModal since
// there's no real asset record for them until save time uploads them.
function previewReferenceImage(openModal, value) {
  if (isPendingUpload(value)) {
    openModal(
      <ImagePreviewModal
        image={{
          url: value.previewUrl,
          thumbnail_url: value.previewUrl,
          name: value.name || "",
        }}
      />,
    );
    return;
  }
  const url = typeof value === "string" ? value : value?.url;
  if (!url) return;
  const thumbnail_url =
    (typeof value === "object" && value?.thumbnail_url) || url;
  openModal(
    <AssetPreviewModal
      asset={{ url, thumbnail_url, name: "", mediaType: "image" }}
    />,
  );
}

// Best-effort display name for a "custom" mesh's uploaded GLB — the node
// itself only stores the blob URL/key (see useFileUpload's `folder` upload
// convention), not the original filename.
function modelFileName(url) {
  if (!url) return null;
  try {
    return decodeURIComponent(url.split("/").pop());
  } catch {
    return url.split("/").pop();
  }
}

export default function PropertiesPanel({
  node,
  isWorldSelected,
  sceneSettings,
  onChangeSceneSettings,
  activeTab = "transform",
  onSelectTab,
  onChange,
}) {
  const { openModal } = useModal();
  const { uploadFile, isUploading } = useFileUpload();

  const selectedGenAiModel =
    GEN_AI_MODELS.find((m) => m.model_id === sceneSettings?.genAiModelId) ??
    GEN_AI_MODELS[0];
  // Several resolutions can share one aspect ratio — the camera only offers
  // the distinct aspect values the selected model's presets actually cover.
  const cameraAspectOptions = [
    ...new Set(selectedGenAiModel.camera_presets.map((p) => p.aspect)),
  ];

  const handleTextureChange = async (value) => {
    const { key, url } = await resolveTextureValue(value, uploadFile);
    onChange({ textureKey: key, textureUrl: url });
  };

  const handleBackgroundTextureChange = async (value) => {
    const { key, url } = await resolveTextureValue(value, uploadFile);
    onChangeSceneSettings({
      backgroundTextureKey: key,
      backgroundTextureUrl: url,
    });
  };

  if (isWorldSelected) {
    const effectiveWorldTab = WORLD_TABS.some((t) => t.id === activeTab)
      ? activeTab
      : "settings";
    return (
      <div id="Scene3DPropertiesPanel" className="flex flex-col h-full">
        <PropertiesTabBar
          tabs={WORLD_TABS}
          activeTab={effectiveWorldTab}
          onSelectTab={onSelectTab}
        />
        <div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1 min-h-0">
          {effectiveWorldTab === "settings" && (
            <>
              <PropertyRow label="Background">
                <ColorPicker
                  value={sceneSettings.backgroundColor}
                  allowAlpha={false}
                  swatchClassName={COLOR_SWATCH_SIZE}
                  onChange={(v) =>
                    onChangeSceneSettings({ backgroundColor: v })
                  }
                />
              </PropertyRow>
              <PropertyRow label="Background Texture">
                <MiniAssetPicker
                  asset={sceneSettings.backgroundTextureUrl}
                  mediaType="image"
                  sizeClass="w-12 h-12"
                  disabled={isUploading}
                  onChange={handleBackgroundTextureChange}
                  onPreview={(value) => previewReferenceImage(openModal, value)}
                />
              </PropertyRow>

              <PropertyRow label="Ambient Light">
                <ColorPicker
                  value={sceneSettings.ambientLightColor}
                  allowAlpha={false}
                  swatchClassName={COLOR_SWATCH_SIZE}
                  onChange={(v) =>
                    onChangeSceneSettings({ ambientLightColor: v })
                  }
                />
              </PropertyRow>
              <PropertyRow label="Ambient Intensity">
                <Input
                  type="number"
                  step={0.05}
                  min={0}
                  value={sceneSettings.ambientLightIntensity}
                  onChange={(e) =>
                    onChangeSceneSettings({
                      ambientLightIntensity: parseFloat(e.target.value) || 0,
                    })
                  }
                  className="text-xs h-7"
                />
              </PropertyRow>

              <PropertyRow
                label="HDR Environment Lighting"
                htmlFor="Scene3DProperties__environmentToggle">
                <Switch
                  id="Scene3DProperties__environmentToggle"
                  checked={sceneSettings.environmentEnabled !== false}
                  onCheckedChange={(checked) =>
                    onChangeSceneSettings({ environmentEnabled: checked })
                  }
                />
              </PropertyRow>
              {sceneSettings.environmentEnabled !== false && (
                <SliderField
                  label="Environment Intensity"
                  value={sceneSettings.environmentIntensity ?? 1}
                  min={0}
                  max={5}
                  step={0.05}
                  onChange={(v) =>
                    onChangeSceneSettings({ environmentIntensity: v })
                  }
                />
              )}

              <PropertyRow label="Quality">
                <Select
                  value={sceneSettings.qualityPreset || "medium"}
                  onValueChange={(v) =>
                    onChangeSceneSettings({ qualityPreset: v })
                  }>
                  <SelectTrigger
                    id="Scene3DProperties__qualityPresetSelect"
                    className="text-xs h-7">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {QUALITY_LEVELS.map((level) => (
                      <SelectItem
                        key={level}
                        value={level}
                        className="text-xs capitalize">
                        {level}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </PropertyRow>
            </>
          )}
          {effectiveWorldTab === "ai" && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-secondary-foreground/50">
                  Reference Images
                </span>
                <ElementsRow
                  values={sceneSettings.aiReferenceImages || []}
                  mediaType="image"
                  max={AI_REFERENCE_IMAGES_MAX}
                  onChange={(next) =>
                    onChangeSceneSettings({ aiReferenceImages: next })
                  }
                  onPreview={(value) => previewReferenceImage(openModal, value)}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-h-0">
                <label className="text-[10px] text-secondary-foreground/50">
                  World Description
                </label>
                <textarea
                  value={sceneSettings.aiDescription || ""}
                  onChange={(e) =>
                    onChangeSceneSettings({ aiDescription: e.target.value })
                  }
                  placeholder="Describe the world this scene takes place in…"
                  className="text-xs font-mono rounded-md border border-secondary-foreground/20 bg-secondary/30 p-2 resize-none w-full flex-1 min-h-24 outline-none focus:border-primary/50"
                />
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (!node) {
    return (
      <div
        id="Scene3DPropertiesPanel"
        className="p-3 text-xs text-secondary-foreground/50">
        Select an object to edit its properties.
      </div>
    );
  }

  const updateTransformAxis = (field, axis, num) => {
    onChange({
      transform: {
        ...node.transform,
        [field]: { ...node.transform[field], [axis]: num },
      },
    });
  };

  // Falls back to "transform" if the previously active tab doesn't apply to
  // this node's type (e.g. switching from a mesh's "material" tab to a light).
  const availableTabs = tabsForNodeType(node.type);
  const effectiveTab = availableTabs.some((t) => t.id === activeTab)
    ? activeTab
    : "transform";
  const customModelFileName =
    node.geometry === "custom" ? modelFileName(node.modelUrl) : null;

  return (
    <div id="Scene3DPropertiesPanel" className="flex flex-col h-full">
      <PropertiesTabBar
        tabs={availableTabs}
        activeTab={effectiveTab}
        onSelectTab={onSelectTab}
      />
      <div className="flex flex-col gap-3 p-3 overflow-y-auto flex-1 min-h-0">
        {effectiveTab === "transform" && (
          <>
            <Vec3Field
              id="position"
              label="Position"
              value={node.transform.position}
              onChange={(axis, num) =>
                updateTransformAxis("position", axis, num)
              }
            />
            <Vec3Field
              id="rotation"
              label="Rotation (deg)"
              value={node.transform.rotation}
              onChange={(axis, num) =>
                updateTransformAxis("rotation", axis, num)
              }
              step={1}
            />
            <Vec3Field
              id="scale"
              label="Scale"
              value={node.transform.scale}
              onChange={(axis, num) => updateTransformAxis("scale", axis, num)}
            />
          </>
        )}

        {effectiveTab === "material" && node.type === "mesh" && (
          <>
            {/* Applies regardless of geometry — a projection composites onto
                a custom/GLB mesh's material the same way it does a
                primitive's (see sceneGraph.js's buildObject3D), so this isn't
                scoped to the primitive-only branch below. */}
            <PropertyRow
              label="Ignore Projections"
              htmlFor={`Scene3DProperties__ignoreProjectionsToggle-${node._id}`}>
              <Switch
                id={`Scene3DProperties__ignoreProjectionsToggle-${node._id}`}
                checked={!!node.ignoreProjections}
                onCheckedChange={(checked) =>
                  onChange({ ignoreProjections: checked })
                }
              />
            </PropertyRow>
            {node.geometry === "custom" ? (
              // An imported GLB brings its own materials — color/texture/
              // roughness/metalness/unlit apply to the primitive geometries
              // built in sceneGraph.js and have nothing to act on here.
              <div className="text-[10px] text-secondary-foreground/50">
                Custom model — material comes from the imported file
                {customModelFileName && (
                  <>
                    :{" "}
                    <span className="text-secondary-foreground/80">
                      {customModelFileName}
                    </span>
                  </>
                )}
              </div>
            ) : (
              <>
                <PropertyRow label="Color">
                  <ColorPicker
                    value={node.color}
                    allowAlpha={false}
                    swatchClassName={COLOR_SWATCH_SIZE}
                    onChange={(v) => onChange({ color: v })}
                  />
                </PropertyRow>
                <PropertyRow label="Texture">
                  <MiniAssetPicker
                    asset={node.textureUrl}
                    mediaType="image"
                    sizeClass="w-12 h-12"
                    disabled={isUploading}
                    onChange={handleTextureChange}
                    onPreview={(value) =>
                      previewReferenceImage(openModal, value)
                    }
                  />
                </PropertyRow>
                <PropertyRow
                  label="Unlit"
                  htmlFor={`Scene3DProperties__unlitToggle-${node._id}`}>
                  <Switch
                    id={`Scene3DProperties__unlitToggle-${node._id}`}
                    checked={!!node.unlit}
                    onCheckedChange={(checked) => onChange({ unlit: checked })}
                  />
                </PropertyRow>
                {!node.unlit && (
                  <>
                    <SliderField
                      label="Roughness"
                      value={node.roughness ?? 1}
                      onChange={(v) => onChange({ roughness: v })}
                    />
                    <SliderField
                      label="Metalness"
                      value={node.metalness ?? 0}
                      onChange={(v) => onChange({ metalness: v })}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}

        {effectiveTab === "ai" &&
          (node.type === "mesh" ||
            node.type === "null" ||
            node.type === "light") && (
            <>
              <div className="flex flex-col gap-1">
                <span className="text-[10px] text-secondary-foreground/50">
                  Reference Images
                </span>
                <ElementsRow
                  values={node.aiReferenceImages || []}
                  mediaType="image"
                  max={AI_REFERENCE_IMAGES_MAX}
                  onChange={(next) => onChange({ aiReferenceImages: next })}
                  onPreview={(value) => previewReferenceImage(openModal, value)}
                />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-h-0">
                <label className="text-[10px] text-secondary-foreground/50">
                  Description
                </label>
                <textarea
                  value={node.aiDescription || ""}
                  onChange={(e) => onChange({ aiDescription: e.target.value })}
                  placeholder="Describe how this object should look for AI generation…"
                  className="text-xs font-mono rounded-md border border-secondary-foreground/20 bg-secondary/30 p-2 resize-none w-full flex-1 min-h-24 outline-none focus:border-primary/50"
                />
              </div>
            </>
          )}

        {effectiveTab === "settings" && node.type === "light" && (
          <>
            <PropertyRow label="Color">
              <ColorPicker
                value={node.lightColor}
                allowAlpha={false}
                swatchClassName={COLOR_SWATCH_SIZE}
                onChange={(v) => onChange({ lightColor: v })}
              />
            </PropertyRow>
            <SliderField
              label="Intensity"
              value={node.intensity ?? 1}
              min={0}
              max={5}
              step={0.05}
              onChange={(v) => onChange({ intensity: v })}
            />
            <PropertyRow
              label="Cast Shadow"
              htmlFor={`Scene3DProperties__castShadowToggle-${node._id}`}>
              <Switch
                id={`Scene3DProperties__castShadowToggle-${node._id}`}
                checked={node.castShadow !== false}
                onCheckedChange={(checked) => onChange({ castShadow: checked })}
              />
            </PropertyRow>
            <PropertyRow
              label="Visible in Viewport"
              htmlFor={`Scene3DProperties__lightVisibleToggle-${node._id}`}>
              <Switch
                id={`Scene3DProperties__lightVisibleToggle-${node._id}`}
                checked={!!node.visibleInViewport}
                onCheckedChange={(checked) =>
                  onChange({ visibleInViewport: checked })
                }
              />
            </PropertyRow>
            {node.visibleInViewport && (
              <SliderField
                label="Visible Size"
                value={node.visibleSize ?? DEFAULT_LIGHT_VISIBLE_SIZE}
                min={0.05}
                max={2}
                step={0.05}
                onChange={(v) => onChange({ visibleSize: v })}
              />
            )}
          </>
        )}

        {effectiveTab === "settings" && node.type === "camera" && (
          <>
            <PropertyRow label="Aspect ratio">
              <Select
                value={closestAspectKey(
                  node.aspect ?? 16 / 9,
                  cameraAspectOptions,
                )}
                onValueChange={(key) =>
                  onChange({ aspect: ratioFromAspectKey(key) })
                }>
                <SelectTrigger className="text-xs h-7">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {cameraAspectOptions.map((key) => (
                    <SelectItem key={key} value={key} className="text-xs">
                      {key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </PropertyRow>
            <PropertyRow label="Field of view">
              <Input
                type="number"
                value={node.fov}
                onChange={(e) =>
                  onChange({ fov: parseFloat(e.target.value) || 1 })
                }
                className="text-xs h-7"
              />
            </PropertyRow>
            <PropertyRow label="Z-Depth Distance">
              <Input
                type="number"
                min={0.1}
                step={1}
                title="World units in front of this camera at which the Depth Map render pass reaches black"
                value={node.zDepthDistance ?? DEFAULT_Z_DEPTH_DISTANCE}
                onChange={(e) =>
                  onChange({
                    zDepthDistance: parseFloat(e.target.value) || 0.1,
                  })
                }
                className="text-xs h-7"
              />
            </PropertyRow>
          </>
        )}
      </div>
    </div>
  );
}
